import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import { Inbox, RefreshCw, PenSquare, Loader2, ChevronDown, Search, Trash2, Mail, Bell, CheckCheck, CheckSquare, Square, Filter, WifiOff, Columns, List } from "lucide-react";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { invokeGmailApi } from "@/lib/gmailApiClient";
import EmailListView from "./EmailListView";
import EmailDetailView from "./EmailDetailView";
import ComposeEmailDialog from "./ComposeEmailDialog";
import TrashListView from "./TrashListView";
import GmailSearchBar from "./GmailSearchBar";
import { playNotificationSound } from "@/utils/notificationSound";
import { showGlobalRetrying, showGlobalSuccess, hideGlobalRetrying } from "@/hooks/useNetworkRetry";
import { useIsMobile } from "@/hooks/use-mobile";

interface Email {
  id: string;
  threadId: string;
  from: string;
  subject: string;
  snippet: string;
  date: string;
  isUnread: boolean;
  hasAttachments: boolean;
  isProcesado?: boolean;
}

interface EmailDetail {
  id: string;
  from: string;
  to: string;
  subject: string;
  body: string;
  date: string;
  attachments: { filename: string; mimeType: string; attachmentId: string; size: number }[];
  isUnread: boolean;
}

interface GmailCuenta {
  id: string;
  email: string;
  nombre: string;
  proposito: string;
  activo: boolean;
  access_token: string | null;
  refresh_token: string | null;
}

interface BandejaEntradaProps {
  cuentas: GmailCuenta[];
}

const BandejaEntrada = ({ cuentas }: BandejaEntradaProps) => {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get initial account from URL params or default to first account
  const getInitialAccount = () => {
    const cuentaParam = searchParams.get("cuenta");
    if (cuentaParam) {
      const found = cuentas.find(c => c.email === cuentaParam);
      if (found) return found.email;
    }
    return cuentas[0]?.email || "";
  };
  
  const [selectedAccount, setSelectedAccount] = useState<string>(getInitialAccount);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  const [selectedEmailIndex, setSelectedEmailIndex] = useState<number>(-1);
  const [composeOpen, setComposeOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [activeTab, setActiveTab] = useState("inbox");
  const [isFromTrash, setIsFromTrash] = useState(false);
  const [markingAllAsRead, setMarkingAllAsRead] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [markingAllInboxAsRead, setMarkingAllInboxAsRead] = useState(false);
  const [filterProcessed, setFilterProcessed] = useState<'all' | 'processed' | 'unprocessed'>('all');
  // View mode: split (two panels) or list-only (full width list)
  const [viewMode, setViewMode] = useState<'split' | 'list-only'>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('email-view-mode') as 'split' | 'list-only') || 'split';
    }
    return 'split';
  });
  // Flag to open first unread email after account switch from notification
  const [pendingOpenUnread, setPendingOpenUnread] = useState<string | null>(null);
  
  // Track previous unread counts to detect new emails
  const previousUnreadCountsRef = useRef<Record<string, number>>({});
  const isInitialLoadRef = useRef(true);
  // Flag to suppress notifications during user-initiated actions
  const suppressNotificationsRef = useRef(false);

  // Sync selectedAccount with URL param when it changes
  useEffect(() => {
    const cuentaParam = searchParams.get("cuenta");
    if (cuentaParam) {
      const found = cuentas.find(c => c.email === cuentaParam);
      if (found && found.email !== selectedAccount) {
        setSelectedAccount(found.email);
        // Clear selection when switching accounts
        setSelectedEmailId(null);
        setSelectedEmailIndex(-1);
      }
    }
  }, [searchParams, cuentas]);

  const selectedCuenta = cuentas.find((c) => c.email === selectedAccount);
  // Track retry attempts for visual indicator
  const retryAttemptRef = useRef(0);

  // Fetch unread counts for all accounts IN PARALLEL - much faster initial load
  const { data: unreadCounts } = useQuery({
    queryKey: ["gmail-unread-counts"],
    queryFn: async () => {
      // Fetch all counts in parallel instead of sequentially
      const results = await Promise.allSettled(
        cuentas.map(cuenta =>
          invokeGmailApi<{ unreadCount?: number }>({
            action: "getUnreadCount",
            email: cuenta.email,
          }).then(response => ({
            email: cuenta.email,
            count: response.data?.unreadCount ?? 0,
            error: response.error,
          }))
        )
      );
      
      // If we were retrying and now succeeded, show success
      if (retryAttemptRef.current > 0) {
        showGlobalSuccess();
        retryAttemptRef.current = 0;
      }
      
      const counts: Record<string, number> = {};
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          counts[result.value.email] = result.value.count;
        } else {
          counts[cuentas[index].email] = 0;
        }
      });
      return counts;
    },
    staleTime: 1000 * 15, // 15 seconds
    refetchInterval: 1000 * 30, // Every 30 seconds - TIEMPO REAL
    retry: 3,
    retryDelay: (attemptIndex) => {
      retryAttemptRef.current = attemptIndex + 1;
      if (attemptIndex > 0) {
        showGlobalRetrying("correos");
      }
      return Math.min(1000 * 2 ** attemptIndex, 10000);
    },
  });

  // Detect new emails and show notifications
  useEffect(() => {
    if (!unreadCounts) return;
    
    // Skip notification on initial load
    if (isInitialLoadRef.current) {
      previousUnreadCountsRef.current = { ...unreadCounts };
      isInitialLoadRef.current = false;
      return;
    }

    // Skip notifications if we're suppressing them (during user-initiated actions like marking as read)
    if (suppressNotificationsRef.current) {
      previousUnreadCountsRef.current = { ...unreadCounts };
      return;
    }

    // Check each account for new emails
    for (const cuenta of cuentas) {
      const currentCount = unreadCounts[cuenta.email] || 0;
      const previousCount = previousUnreadCountsRef.current[cuenta.email] || 0;
      
      // If unread count increased, we have new emails
      if (currentCount > previousCount) {
        const newEmailsCount = currentCount - previousCount;
        
        // Play notification sound
        playNotificationSound();
        
        // Show toast notification
        toast.info(
          `${newEmailsCount} nuevo${newEmailsCount > 1 ? 's' : ''} correo${newEmailsCount > 1 ? 's' : ''} en ${cuenta.nombre}`,
          {
            description: cuenta.email,
            icon: <Bell className="h-4 w-4" />,
            action: {
              label: "Ver",
              onClick: () => {
                setSelectedAccount(cuenta.email);
                setActiveTab("inbox");
                // Set flag to open first unread email after emails load
                setPendingOpenUnread(cuenta.email);
              },
            },
            duration: 8000,
          }
        );

        // Request browser notification permission and show notification
        if (typeof Notification !== 'undefined') {
          if (Notification.permission === "granted") {
            new Notification(`Nuevo correo en ${cuenta.nombre}`, {
              body: `${newEmailsCount} correo${newEmailsCount > 1 ? 's' : ''} sin leer`,
              icon: "/favicon.ico",
            });
          } else if (Notification.permission !== "denied") {
            Notification.requestPermission();
          }
        }
      }
    }
    
  // Update previous counts
    previousUnreadCountsRef.current = { ...unreadCounts };
  }, [unreadCounts, cuentas]);

  // State for pagination
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [allEmails, setAllEmails] = useState<Email[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Track retry attempts for email list
  const emailRetryAttemptRef = useRef(0);

  // Query para obtener correos procesados (solo de pedidos acumulativos en borrador)
  const { data: correosProcesados } = useQuery({
    queryKey: ["correos-procesados"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pedidos_acumulativos")
        .select("correos_procesados")
        .eq("status", "borrador"); // Solo pedidos en borrador, no finalizados
      
      if (error) throw error;
      
      // Aplanar todos los arrays de correos_procesados en un solo Set
      const allProcessed = new Set<string>();
      data?.forEach(pedido => {
        pedido.correos_procesados?.forEach(emailId => allProcessed.add(emailId));
      });
      
      return allProcessed;
    },
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 30, // Refetch every 30 seconds
  });

  // Fetch email list - every 60 seconds
  const {
    data: emailsData,
    isLoading,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ["gmail-inbox", selectedAccount, activeSearch],
    queryFn: async () => {
      if (!selectedAccount) return { messages: [], nextPageToken: null };

      const response = await invokeGmailApi<{ messages?: Email[]; nextPageToken?: string | null }>({
        action: "list",
        email: selectedAccount,
        maxResults: 25,
        searchQuery: activeSearch || undefined,
      });

      if (response.error) {
        // Avoid a blank-screen loop: surface a friendly error and let react-query retry.
        throw new Error(String((response.error as any)?.message ?? "Error cargando correos"));
      }

      // If we were retrying and now succeeded, show success
      if (emailRetryAttemptRef.current > 0) {
        showGlobalSuccess();
        emailRetryAttemptRef.current = 0;
      }

      return {
        messages: (response.data?.messages as Email[]) || [],
        nextPageToken: response.data?.nextPageToken || null,
      };
    },
    enabled: !!selectedAccount && activeTab === "inbox",
    staleTime: 1000 * 15, // 15 seconds - data considered fresh
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchInterval: 1000 * 30, // Refetch every 30 seconds - TIEMPO REAL
    refetchOnWindowFocus: true, // Refresh when user returns
    retry: 3,
    retryDelay: (attemptIndex) => {
      emailRetryAttemptRef.current = attemptIndex + 1;
      if (attemptIndex > 0) {
        showGlobalRetrying("correos");
      }
      return Math.min(1000 * 2 ** attemptIndex, 10000);
    },
  });

  // Update allEmails and nextPageToken when data loads or account/search changes
  // Track the current account/search to detect when they change
  const lastAccountRef = useRef(selectedAccount);
  const lastSearchRef = useRef(activeSearch);
  
  useEffect(() => {
    // Check if account or search changed - reset state
    const accountChanged = lastAccountRef.current !== selectedAccount;
    const searchChanged = lastSearchRef.current !== activeSearch;
    
    if (accountChanged || searchChanged) {
      lastAccountRef.current = selectedAccount;
      lastSearchRef.current = activeSearch;
      setAllEmails([]);
      setNextPageToken(null);
    }
    
    // Always update with new data when available
    if (emailsData && emailsData.messages) {
      setAllEmails(emailsData.messages);
      setNextPageToken(emailsData.nextPageToken);
    }
  }, [emailsData, selectedAccount, activeSearch]);

  // Load more emails
  const handleLoadMore = async () => {
    if (!nextPageToken || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      const response = await invokeGmailApi<{ messages?: Email[]; nextPageToken?: string | null }>({
        action: "list",
        email: selectedAccount,
        maxResults: 50,
        searchQuery: activeSearch || undefined,
        pageToken: nextPageToken,
      });

      if (response.error) {
        throw new Error(String((response.error as any)?.message ?? "Error al cargar"));
      }

      const newMessages = (response.data?.messages as Email[]) || [];
      setAllEmails(prev => [...prev, ...newMessages]);
      setNextPageToken(response.data?.nextPageToken || null);
    } catch (error) {
      console.error("Error loading more emails:", error);
      toast.error("Error al cargar más correos");
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Use allEmails instead of emails for display
  const emails = allEmails;

  // Auto-open first unread email when triggered from notification
  useEffect(() => {
    if (pendingOpenUnread && emails && emails.length > 0 && selectedAccount === pendingOpenUnread && !isLoading) {
      // Find the first unread email
      const firstUnread = emails.find(e => e.isUnread);
      if (firstUnread) {
        const index = emails.indexOf(firstUnread);
        setSelectedEmailId(firstUnread.id);
        setSelectedEmailIndex(index);
        setIsFromTrash(false);
      } else {
        // If no unread, open the first email
        setSelectedEmailId(emails[0].id);
        setSelectedEmailIndex(0);
        setIsFromTrash(false);
      }
      setPendingOpenUnread(null);
    }
  }, [pendingOpenUnread, emails, selectedAccount, isLoading]);

  // Fetch selected email detail
  const { data: emailDetail, isLoading: isLoadingDetail, isError: isEmailError } = useQuery({
    queryKey: ["gmail-email", selectedAccount, selectedEmailId],
    queryFn: async () => {
      if (!selectedEmailId || !selectedAccount) return null;

      const response = await invokeGmailApi<EmailDetail>({
        action: "read",
        email: selectedAccount,
        messageId: selectedEmailId,
      });

      // Handle 404 - email was deleted (check both error object and data)
      const errorMessage =
        String((response.error as any)?.message ?? "") ||
        String((response.data as { error?: string } | null)?.error ?? "");
      
      if (errorMessage.toLowerCase().includes("no encontrado") || 
          errorMessage.toLowerCase().includes("eliminado") ||
          errorMessage.toLowerCase().includes("not found")) {
        // Remove from local list
        setAllEmails(prev => prev.filter(e => e.id !== selectedEmailId));
        toast.info("Este correo ya no existe o fue eliminado");
        setSelectedEmailId(null);
        setSelectedEmailIndex(-1);
        return null;
      }

      if (response.error) {
        throw new Error(String((response.error as any)?.message ?? "Error al abrir correo"));
      }

      return response.data as EmailDetail;
    },
    enabled: !!selectedEmailId && !!selectedAccount,
    retry: false, // Don't retry on 404 errors
  });

  // Mark email as read when viewing - with optimistic update
  useEffect(() => {
    if (emailDetail?.isUnread && selectedEmailId && !isFromTrash) {
      // Suppress notifications during this action
      suppressNotificationsRef.current = true;
      
      // Optimistic update: immediately update local state
      setAllEmails(prev => 
        prev.map(e => e.id === selectedEmailId ? { ...e, isUnread: false } : e)
      );
      
      // Optimistic update: decrement unread count and sync the ref
      queryClient.setQueryData(
        ["gmail-unread-counts"],
        (oldData: Record<string, number> | undefined) => {
          if (!oldData) return oldData;
          const newCounts = {
            ...oldData,
            [selectedAccount]: Math.max(0, (oldData[selectedAccount] || 0) - 1),
          };
          previousUnreadCountsRef.current = { ...newCounts };
          return newCounts;
        }
      );

      // Call API to mark as read
      invokeGmailApi({
        action: "markAsRead",
        email: selectedAccount,
        messageId: selectedEmailId,
      }).then(() => {
        // Refresh to ensure sync with server
        queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
        // Re-enable notifications after a short delay
        setTimeout(() => {
          suppressNotificationsRef.current = false;
        }, 2000);
      });
    }
  }, [emailDetail?.isUnread, selectedEmailId, selectedAccount, isFromTrash, queryClient, activeSearch]);

  const handleAccountChange = (email: string) => {
    setSelectedAccount(email);
    setSelectedEmailId(null);
    setSelectedEmailIndex(-1);
    setSearchQuery("");
    setActiveSearch("");
    setSelectedEmailIds(new Set());
    setSelectionMode(false);
    // Update URL param
    setSearchParams({ cuenta: email });
  };
  
  // Listen for URL param changes to switch accounts
  useEffect(() => {
    const cuentaParam = searchParams.get("cuenta");
    if (cuentaParam && cuentaParam !== selectedAccount) {
      const found = cuentas.find(c => c.email === cuentaParam);
      if (found) {
        setSelectedAccount(found.email);
        setSelectedEmailId(null);
        setSelectedEmailIndex(-1);
        setSearchQuery("");
        setActiveSearch("");
        setSelectedEmailIds(new Set());
        setSelectionMode(false);
      }
    }
  }, [searchParams, cuentas]);

  // Toggle selection of an email
  const handleToggleSelect = (id: string) => {
    setSelectedEmailIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Select all emails
  const handleSelectAll = () => {
    if (emails) {
      setSelectedEmailIds(new Set(emails.map(e => e.id)));
    }
  };

  // Clear selection
  const handleClearSelection = () => {
    setSelectedEmailIds(new Set());
    setSelectionMode(false);
  };

  // Delete selected emails - optimistic, non-blocking
  const handleDeleteSelected = () => {
    if (selectedEmailIds.size === 0) return;

    const selectedIdsArray = Array.from(selectedEmailIds);
    const unreadSelectedCount = emails?.filter(e => selectedIdsArray.includes(e.id) && e.isUnread).length || 0;
    const count = selectedEmailIds.size;

    suppressNotificationsRef.current = true;
    
    // Optimistic update: remove emails from local state immediately
    setAllEmails(prev => prev.filter(e => !selectedIdsArray.includes(e.id)));
    
    // Optimistic update: decrement unread count
    queryClient.setQueryData(
      ["gmail-unread-counts"],
      (oldData: Record<string, number> | undefined) => {
        if (!oldData) return oldData;
        const newCounts = {
          ...oldData,
          [selectedAccount]: Math.max(0, (oldData[selectedAccount] || 0) - unreadSelectedCount),
        };
        previousUnreadCountsRef.current = { ...newCounts };
        return newCounts;
      }
    );

    // Clear selection immediately - UI responds fast
    toast.success(`${count} correo(s) eliminado(s)`);
    setSelectedEmailIds(new Set());
    setSelectionMode(false);

    // Fire batch API call in background (non-blocking)
    supabase.functions.invoke("gmail-api", {
      body: {
        action: "trash",
        email: selectedAccount,
        messageId: selectedIdsArray,
      },
    }).then(() => {
      // Sync silently
      queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
      queryClient.invalidateQueries({ queryKey: ["gmail-trash", selectedAccount] });
    }).catch((error) => {
      console.error("Error deleting:", error);
      // On error, refresh to get actual state
      queryClient.invalidateQueries({ queryKey: ["gmail-inbox", selectedAccount] });
      queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
    }).finally(() => {
      setTimeout(() => {
        suppressNotificationsRef.current = false;
      }, 2000);
    });
  };

  // Mark selected emails as read - optimistic, non-blocking
  const handleMarkSelectedAsRead = () => {
    if (selectedEmailIds.size === 0) return;

    const selectedIdsArray = Array.from(selectedEmailIds);
    const unreadSelectedCount = emails?.filter(e => selectedIdsArray.includes(e.id) && e.isUnread).length || 0;
    const count = selectedEmailIds.size;

    // Suppress notifications during this action
    suppressNotificationsRef.current = true;
    
    // Optimistic update: immediately update local state
    setAllEmails(prev => 
      prev.map(e => selectedIdsArray.includes(e.id) ? { ...e, isUnread: false } : e)
    );
    
    // Optimistic update: decrement unread count
    queryClient.setQueryData(
      ["gmail-unread-counts"],
      (oldData: Record<string, number> | undefined) => {
        if (!oldData) return oldData;
        const newCounts = {
          ...oldData,
          [selectedAccount]: Math.max(0, (oldData[selectedAccount] || 0) - unreadSelectedCount),
        };
        previousUnreadCountsRef.current = { ...newCounts };
        return newCounts;
      }
    );

    // Clear selection immediately - UI responds fast
    toast.success(`${count} correo(s) marcado(s) como leído(s)`);
    setSelectedEmailIds(new Set());
    setSelectionMode(false);

    // Fire API call in background (non-blocking)
    supabase.functions.invoke("gmail-api", {
      body: {
        action: "markAsRead",
        email: selectedAccount,
        messageId: selectedIdsArray,
      },
    }).then(() => {
      // Sync with server silently
      queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
    }).catch((error) => {
      console.error("Error marking as read:", error);
      // On error, refresh to get actual state
      queryClient.invalidateQueries({ queryKey: ["gmail-inbox", selectedAccount] });
      queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
    }).finally(() => {
      setTimeout(() => {
        suppressNotificationsRef.current = false;
      }, 2000);
    });
  };

  const handleEmailDeleted = () => {
    setSelectedEmailId(null);
    setSelectedEmailIndex(-1);
    setIsFromTrash(false);
    queryClient.invalidateQueries({
      queryKey: ["gmail-inbox", selectedAccount],
    });
    queryClient.invalidateQueries({
      queryKey: ["gmail-trash", selectedAccount],
    });
    queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
  };

  const handleBack = () => {
    setSelectedEmailId(null);
    setSelectedEmailIndex(-1);
    setIsFromTrash(false);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setActiveSearch(searchQuery);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setActiveSearch("");
  };

  const handleSelectEmail = (id: string, fromTrash: boolean = false, index: number = -1) => {
    setSelectedEmailId(id);
    setSelectedEmailIndex(index);
    setIsFromTrash(fromTrash);
  };

  // Navigate to next email
  const handleNavigateNext = () => {
    if (emails && selectedEmailIndex < emails.length - 1) {
      const nextIndex = selectedEmailIndex + 1;
      setSelectedEmailIndex(nextIndex);
      setSelectedEmailId(emails[nextIndex].id);
    }
  };

  // Navigate to previous email
  const handleNavigatePrev = () => {
    if (emails && selectedEmailIndex > 0) {
      const prevIndex = selectedEmailIndex - 1;
      setSelectedEmailIndex(prevIndex);
      setSelectedEmailId(emails[prevIndex].id);
    }
  };

   // Mark all emails as read
  const handleMarkAllAsRead = async () => {
    if (!emails || emails.length === 0) return;
    
    const unreadEmails = emails.filter(e => e.isUnread);
    if (unreadEmails.length === 0) {
      toast.info("No hay correos sin leer");
      return;
    }

    setMarkingAllAsRead(true);
    suppressNotificationsRef.current = true;
    
    try {
      // Mark all unread emails as read
      await Promise.all(
        unreadEmails.map(email =>
          supabase.functions.invoke("gmail-api", {
            body: {
              action: "markAsRead",
              email: selectedAccount,
              messageId: email.id,
            },
          })
        )
      );

      toast.success(`${unreadEmails.length} correo(s) marcado(s) como leído(s)`);
      
      // Refresh data
      await queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
      await queryClient.invalidateQueries({ queryKey: ["gmail-inbox", selectedAccount] });
    } catch (error) {
      console.error("Error marking all as read:", error);
      toast.error("Error al marcar correos como leídos");
    } finally {
      setMarkingAllAsRead(false);
      setTimeout(() => {
        suppressNotificationsRef.current = false;
      }, 2000);
    }
  };

  // Mark ALL emails in ALL accounts as read (resets everything to 0)
  const handleMarkAllAccountsAsRead = async () => {
    const totalUnread = Object.values(unreadCounts || {}).reduce((sum, count) => sum + count, 0);
    
    if (totalUnread === 0) {
      toast.info("No hay correos sin leer en ninguna cuenta");
      return;
    }

    const confirmed = window.confirm(
      `¿Estás seguro de marcar TODOS los ${totalUnread.toLocaleString()} correos sin leer como leídos en TODAS las cuentas?\n\nEsto puede tardar unos minutos.`
    );
    
    if (!confirmed) return;

    setMarkingAllInboxAsRead(true);
    suppressNotificationsRef.current = true;
    
    toast.info("Marcando todos los correos como leídos... Esto puede tardar unos minutos.");

    try {
      // Mark all emails as read for ALL accounts in parallel
      const results = await Promise.allSettled(
        cuentas.map(cuenta =>
          supabase.functions.invoke("gmail-api", {
            body: {
              action: "markAllInboxAsRead",
              email: cuenta.email,
            },
          })
        )
      );

      let totalMarked = 0;
      results.forEach((result, index) => {
        if (result.status === "fulfilled" && result.value.data?.totalMarked) {
          totalMarked += result.value.data.totalMarked;
          console.log(`${cuentas[index].email}: ${result.value.data.totalMarked} emails marked`);
        }
      });

      // Reset all counts to 0 optimistically
      queryClient.setQueryData(["gmail-unread-counts"], () => {
        const zeroCounts: Record<string, number> = {};
        cuentas.forEach(c => { zeroCounts[c.email] = 0; });
        previousUnreadCountsRef.current = { ...zeroCounts };
        return zeroCounts;
      });

      // Clear local emails state
      setAllEmails(prev => prev.map(e => ({ ...e, isUnread: false })));

      toast.success(`¡Listo! ${totalMarked.toLocaleString()} correos marcados como leídos`);
      
      // Refresh data
      queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
      queryClient.invalidateQueries({ queryKey: ["gmail-inbox"] });
    } catch (error) {
      console.error("Error marking all accounts as read:", error);
      toast.error("Error al marcar correos como leídos");
    } finally {
      setMarkingAllInboxAsRead(false);
      setTimeout(() => {
        suppressNotificationsRef.current = false;
      }, 2000);
    }
  };

  // En móvil o modo solo lista con correo seleccionado: mostrar detalle a pantalla completa
  if ((isMobile || viewMode === 'list-only') && selectedEmailId && emailDetail) {
    return (
      <EmailDetailView
        email={emailDetail}
        cuentaEmail={selectedAccount}
        cuentas={cuentas}
        onBack={handleBack}
        onDeleted={handleEmailDeleted}
        onNavigateNext={handleNavigateNext}
        onNavigatePrev={handleNavigatePrev}
        hasNext={emails ? selectedEmailIndex < emails.length - 1 : false}
        hasPrev={selectedEmailIndex > 0}
        isFromTrash={isFromTrash}
      />
    );
  }

  // Layout móvil estilo Gmail
  if (isMobile) {
    return (
      <>
        <div className="space-y-3">
          {/* Barra de búsqueda estilo Gmail */}
          <GmailSearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onSearchSubmit={() => setActiveSearch(searchQuery)}
            onClearSearch={clearSearch}
            cuentas={cuentas}
            selectedAccount={selectedAccount}
            onAccountChange={handleAccountChange}
            unreadCounts={unreadCounts}
          />

          {activeSearch && (
            <div className="text-sm text-muted-foreground px-1">
              Resultados para: <span className="font-medium">"{activeSearch}"</span>
            </div>
          )}

          {/* Tabs simplificados para móvil */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between gap-2 overflow-x-auto">
              <TabsList className="h-9">
                <TabsTrigger value="inbox" className="gap-1.5 text-xs px-3">
                  <Inbox className="h-4 w-4" />
                  Entrada
                  {unreadCounts?.[selectedAccount] ? (
                    <Badge variant="destructive" className="text-[10px] px-1.5 h-4 ml-1">
                      {unreadCounts[selectedAccount]}
                    </Badge>
                  ) : null}
                </TabsTrigger>
                <TabsTrigger value="trash" className="gap-1.5 text-xs px-3">
                  <Trash2 className="h-4 w-4" />
                  Papelera
                </TabsTrigger>
              </TabsList>

              {/* Filtros compactos móvil */}
              {activeTab === "inbox" && (
                <div className="flex items-center gap-1">
                  <Button
                    variant={showOnlyUnread ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setShowOnlyUnread(!showOnlyUnread)}
                    className="h-8 px-2"
                  >
                    <Filter className="h-4 w-4" />
                  </Button>
                  
                  {!selectionMode ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectionMode(true)}
                      className="h-8 px-2"
                    >
                      <Square className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearSelection}
                      className="h-8 px-2"
                    >
                      ✕
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Barra de selección móvil */}
            {selectionMode && selectedEmailIds.size > 0 && (
              <div className="flex items-center gap-2 py-2 px-1">
                <Badge variant="secondary" className="text-xs">
                  {selectedEmailIds.size} seleccionado(s)
                </Badge>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSelectAll}
                  className="h-7 text-xs"
                >
                  Todos
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleMarkSelectedAsRead}
                  disabled={markingAllAsRead}
                  className="h-7 text-xs"
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Leído
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDeleteSelected}
                  disabled={deletingSelected}
                  className="h-7 text-xs"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}

            <TabsContent value="inbox" className="mt-2">
              <EmailListView
                emails={(showOnlyUnread ? emails.filter(e => e.isUnread) : emails)
                  .map(email => ({
                    ...email,
                    isProcesado: correosProcesados?.has(email.id) || false,
                  }))
                  .filter(email => {
                    if (filterProcessed === 'processed') return email.isProcesado;
                    if (filterProcessed === 'unprocessed') return !email.isProcesado;
                    return true;
                  })
                }
                isLoading={isLoading}
                onSelectEmail={(id, index) => handleSelectEmail(id, false, index)}
                onRefresh={() => refetch()}
                isRefreshing={isRefetching}
                selectedIds={selectedEmailIds}
                onToggleSelect={handleToggleSelect}
                selectionMode={selectionMode}
                hasMore={!!nextPageToken}
                isLoadingMore={isLoadingMore}
                onLoadMore={handleLoadMore}
                accountTag={selectedCuenta?.nombre}
              />
            </TabsContent>

            <TabsContent value="trash" className="mt-2">
              <TrashListView
                email={selectedAccount}
                onSelectEmail={(id) => handleSelectEmail(id, true)}
                onEmailRecovered={handleEmailDeleted}
              />
            </TabsContent>
          </Tabs>

          {/* Loading overlay for email detail */}
          {isLoadingDetail && (
            <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}
        </div>

        {/* FAB para redactar - estilo Gmail */}
        <Button
          onClick={() => setComposeOpen(true)}
          className="fixed bottom-20 right-4 h-14 px-5 rounded-full shadow-lg z-40 gap-2"
        >
          <PenSquare className="h-5 w-5" />
          <span className="font-medium">Redactar</span>
        </Button>

        {/* Compose email dialog */}
        <ComposeEmailDialog
          open={composeOpen}
          onOpenChange={setComposeOpen}
          fromEmail={selectedAccount}
          cuentas={cuentas}
          onSuccess={() => refetch()}
        />
      </>
    );
  }

  // Layout desktop con dos paneles estilo Gmail
  return (
    <>
      <div className="flex flex-col h-[calc(100vh-180px)] overflow-hidden">
        {/* Header with account dropdown and actions */}
        <div className="flex flex-col gap-4 shrink-0 pb-4">
          {/* Row 1: Account selector */}
          <div className="flex items-center gap-3 min-w-0">
            <Mail className="h-5 w-5 text-primary shrink-0" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="min-w-0 max-w-[300px] flex-1 sm:flex-none sm:w-[300px] justify-between">
                  <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                    <span className="truncate">{selectedCuenta?.nombre || "Seleccionar cuenta"}</span>
                    {unreadCounts?.[selectedAccount] ? (
                      <Badge variant="destructive" className="text-xs shrink-0">
                        {unreadCounts[selectedAccount]}
                      </Badge>
                    ) : null}
                  </div>
                  <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[300px] bg-popover" align="start">
                {cuentas.map((cuenta) => (
                  <DropdownMenuItem
                    key={cuenta.id}
                    onClick={() => handleAccountChange(cuenta.email)}
                    className="flex items-center justify-between"
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{cuenta.nombre}</span>
                      <span className="text-xs text-muted-foreground">{cuenta.email}</span>
                    </div>
                    {unreadCounts?.[cuenta.email] ? (
                      <Badge variant="destructive" className="text-xs">
                        {unreadCounts[cuenta.email]}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-xs">
                        {cuenta.proposito}
                      </Badge>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Row 2: Action buttons - responsive con flex-wrap */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={markingAllAsRead || isLoading}
              title="Marcar todos como leídos"
              className="whitespace-nowrap"
            >
              {markingAllAsRead ? (
                <Loader2 className="h-4 w-4 lg:mr-2 animate-spin" />
              ) : (
                <CheckCheck className="h-4 w-4 lg:mr-2" />
              )}
              <span className="hidden lg:inline">Marcar leídos</span>
            </Button>

            <Button
              variant="secondary"
              size="sm"
              onClick={handleMarkAllAccountsAsRead}
              disabled={markingAllInboxAsRead}
              title="Marcar TODOS los correos de TODAS las cuentas como leídos"
              className="bg-amber-500 hover:bg-amber-600 text-white whitespace-nowrap"
            >
              {markingAllInboxAsRead ? (
                <Loader2 className="h-4 w-4 lg:mr-2 animate-spin" />
              ) : (
                <CheckCheck className="h-4 w-4 lg:mr-2" />
              )}
              <span className="hidden lg:inline">Resetear TODO</span>
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                refetch();
                queryClient.invalidateQueries({ queryKey: ["gmail-unread-counts"] });
              }}
              disabled={isRefetching}
              className="whitespace-nowrap"
            >
              <RefreshCw
                className={`h-4 w-4 lg:mr-2 ${isRefetching ? "animate-spin" : ""}`}
              />
              <span className="hidden lg:inline">Actualizar</span>
            </Button>

            <Button size="sm" onClick={() => setComposeOpen(true)} className="whitespace-nowrap">
              <PenSquare className="h-4 w-4 lg:mr-2" />
              <span className="hidden lg:inline">Nuevo correo</span>
            </Button>

            {/* View mode toggle */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const newMode = viewMode === 'split' ? 'list-only' : 'split';
                setViewMode(newMode);
                localStorage.setItem('email-view-mode', newMode);
              }}
              title={viewMode === 'split' ? 'Cambiar a solo lista' : 'Cambiar a vista dividida'}
              className="whitespace-nowrap"
            >
              {viewMode === 'split' ? (
                <List className="h-4 w-4 lg:mr-2" />
              ) : (
                <Columns className="h-4 w-4 lg:mr-2" />
              )}
              <span className="hidden lg:inline">{viewMode === 'split' ? 'Solo lista' : 'Dividir'}</span>
            </Button>
          </div>
        </div>

        {/* Search bar - responsive */}
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 shrink-0 pb-4">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar correos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 w-full"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button type="submit" variant="secondary" className="flex-1 sm:flex-none">
              Buscar
            </Button>
            {activeSearch && (
              <Button type="button" variant="ghost" onClick={clearSearch}>
                Limpiar
              </Button>
            )}
          </div>
        </form>

        {activeSearch && (
          <div className="text-sm text-muted-foreground shrink-0 pb-2">
            Resultados para: <span className="font-medium">"{activeSearch}"</span>
          </div>
        )}

        {/* Main content area - Two panels or full width */}
        <div className="flex-1 flex gap-4 overflow-hidden min-h-0">
          {/* Left Panel: Email List */}
          <div className={cn(
            "flex flex-col overflow-hidden h-full",
            // En modo split con correo seleccionado: ancho fijo
            // En modo solo lista: todo el ancho
            viewMode === 'split' && selectedEmailId ? "w-[400px] shrink-0" : "flex-1"
          )}>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col h-full">
              <div className="flex flex-wrap items-center justify-between gap-2 shrink-0">
                <TabsList>
                  <TabsTrigger value="inbox" className="gap-2">
                    <Inbox className="h-4 w-4" />
                    Bandeja de entrada
                    {unreadCounts?.[selectedAccount] ? (
                      <Badge variant="destructive" className="text-xs ml-1">
                        {unreadCounts[selectedAccount]}
                      </Badge>
                    ) : null}
                  </TabsTrigger>
                  <TabsTrigger value="trash" className="gap-2">
                    <Trash2 className="h-4 w-4" />
                    Papelera
                  </TabsTrigger>
                </TabsList>

                {/* Filter and Selection controls - responsive */}
                {activeTab === "inbox" && (
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant={showOnlyUnread ? "default" : "outline"}
                      size="sm"
                      onClick={() => setShowOnlyUnread(!showOnlyUnread)}
                      title="Mostrar solo no leídos"
                      className="whitespace-nowrap"
                    >
                      <Filter className="h-4 w-4 lg:mr-2" />
                      <span className="hidden lg:inline">No leídos</span>
                    </Button>
                    
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant={filterProcessed !== 'all' ? "default" : "outline"}
                          size="sm"
                          className="whitespace-nowrap"
                        >
                          <CheckCheck className="h-4 w-4 lg:mr-2" />
                          <span className="hidden lg:inline">
                            {filterProcessed === 'all' ? 'Todos' : 
                             filterProcessed === 'processed' ? 'Procesados' : 'No procesados'}
                          </span>
                          <ChevronDown className="h-4 w-4 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-popover">
                        <DropdownMenuItem onClick={() => setFilterProcessed('all')}>
                          Todos los correos
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setFilterProcessed('processed')}>
                          Solo procesados
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setFilterProcessed('unprocessed')}>
                          Solo no procesados
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    {!selectionMode ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectionMode(true)}
                        className="whitespace-nowrap"
                      >
                        <Square className="h-4 w-4 lg:mr-2" />
                        <span className="hidden lg:inline">Seleccionar</span>
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleSelectAll}
                        >
                          <CheckSquare className="h-4 w-4 mr-2" />
                          Seleccionar todos
                        </Button>
                        {selectedEmailIds.size > 0 && (
                          <>
                            <Badge variant="secondary">
                              {selectedEmailIds.size} seleccionado(s)
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleMarkSelectedAsRead}
                              disabled={markingAllAsRead}
                            >
                              {markingAllAsRead ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <CheckCheck className="h-4 w-4 mr-2" />
                              )}
                              Marcar leídos
                            </Button>
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={handleDeleteSelected}
                              disabled={deletingSelected}
                            >
                              {deletingSelected ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4 mr-2" />
                              )}
                              Eliminar
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleClearSelection}
                        >
                          Cancelar
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <TabsContent value="inbox" className="mt-4 flex-1 overflow-hidden">
                <EmailListView
                  emails={(showOnlyUnread ? emails.filter(e => e.isUnread) : emails)
                    .map(email => ({
                      ...email,
                      isProcesado: correosProcesados?.has(email.id) || false,
                    }))
                    .filter(email => {
                      if (filterProcessed === 'processed') return email.isProcesado;
                      if (filterProcessed === 'unprocessed') return !email.isProcesado;
                      return true;
                    })
                  }
                  isLoading={isLoading}
                  onSelectEmail={(id, index) => handleSelectEmail(id, false, index)}
                  onRefresh={() => refetch()}
                  isRefreshing={isRefetching}
                  selectedIds={selectedEmailIds}
                  onToggleSelect={handleToggleSelect}
                  selectionMode={selectionMode}
                  hasMore={!!nextPageToken}
                  isLoadingMore={isLoadingMore}
                  onLoadMore={handleLoadMore}
                  selectedEmailId={selectedEmailId}
                />
              </TabsContent>

              <TabsContent value="trash" className="mt-4 flex-1 overflow-hidden">
                <TrashListView
                  email={selectedAccount}
                  onSelectEmail={(id) => handleSelectEmail(id, true)}
                  onEmailRecovered={handleEmailDeleted}
                />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Panel: Email Detail or Empty State - Only in split mode */}
          {viewMode === 'split' && (
            <>
              {selectedEmailId && emailDetail ? (
                <div className="flex-1 overflow-hidden border rounded-lg">
                  <ScrollArea className="h-full">
                    <div className="p-4">
                      <EmailDetailView
                        email={emailDetail}
                        cuentaEmail={selectedAccount}
                        cuentas={cuentas}
                        onBack={handleBack}
                        onDeleted={handleEmailDeleted}
                        onNavigateNext={handleNavigateNext}
                        onNavigatePrev={handleNavigatePrev}
                        hasNext={emails ? selectedEmailIndex < emails.length - 1 : false}
                        hasPrev={selectedEmailIndex > 0}
                        isFromTrash={isFromTrash}
                        embedded={true}
                      />
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-muted-foreground border rounded-lg">
                  <div className="text-center">
                    <Mail className="h-16 w-16 mx-auto mb-4 opacity-50" />
                    <p>Selecciona un correo para ver su contenido</p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Loading overlay for email detail */}
        {isLoadingDetail && (
          <div className="fixed inset-0 bg-background/80 flex items-center justify-center z-50">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}
      </div>

      {/* Compose email dialog with account selector */}
      <ComposeEmailDialog
        open={composeOpen}
        onOpenChange={setComposeOpen}
        fromEmail={selectedAccount}
        cuentas={cuentas}
        onSuccess={() => refetch()}
      />
    </>
  );
};

export default BandejaEntrada;